import { html, LitElement, TemplateResult, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

// See https://vitejs.dev/guide/features#disabling-css-injection-into-the-page for information about ?inline present here
import innerStyles from './nuvance-decoupled-search.inner.pcss?inline';
import globalStyles from './nuvance-decoupled-search.global.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';

/**
 * NuvanceDecoupledSearch
 * This component handles decoupled search functionality by fetching content from API endpoints
 * and updating the display without page reloads.
 *
 * @element nuvance-decoupled-search
 * @slot - Default slot for search results content
 */
@customElement('nuvance-decoupled-search')
export class NuvanceDecoupledSearch extends LitElement {
  // AdoptedStyleSheets controller for managing component styles
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  /**
   * The API endpoint to fetch search results from
   * e.g., /api/site-search/main or /api/site-search/locations
   */
  @property({ type: String, reflect: true })
  endpoint = '';

  /**
   * The query selector to grab content from the API response
   */
  @property({ type: String, reflect: true, attribute: 'content-selector' })
  contentSelector = '.view-id-search';

  /**
   * Parameter name for storing the content path in the URL
   */
  @property({ type: String, reflect: true, attribute: 'content-path-param' })
  contentPathParam: string | null = null;

  /**
   * The path to fetch content from
   */
  @property({ type: String, reflect: true })
  contentPath = '';

  /**
   * The query parameter name for the search string
   */
  @property({ type: String, reflect: true, attribute: 'search-param' })
  searchParam = 'search_api_fulltext';

  /**
   * Flag to indicate this is a Kyruus search implementation
   * When true, special behavior for Kyruus integration is enabled:
   * - Location filter forms will preserve current keyword search
   * - Multiple filter forms can be combined without resetting search state
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-kyruus' })
  isKyruus = false;

  /**
   * The current search query string
   *
   * When this property is set to a non-empty value, it will reset other search parameters
   * (like pagination and filters) as it represents a new search.
   *
   * This property works together with the searchParam property to form the query parameter
   * that will be sent to the endpoint: [searchParam]=[query]
   *
   * For example: search_api_fulltext=example query
   *
   * This property is meant to be set by an external component.
   */
  @property({ type: String, reflect: true })
  query = '';

  /**
   * Debug mode flag that enables logging of component lifecycle events and state changes.
   * When enabled, the component will output detailed logs about initialization, updates,
   * and internal state changes to help with development and troubleshooting.
   *
   * @property {boolean} debug - When true, enables debug logging
   * @default false
   */
  @property({ type: Boolean, attribute: 'debug' })
  debug = false;

  /**
   * Selector for a form to intercept submissions from.
   * When set, the component will attach a submit event listener to the form
   * and prevent the default submission behavior.
   *
   * @property {string} formSelector - CSS selector for the form to intercept
   * @default '' (empty string, which means no form interception)
   */
  @property({ type: String, reflect: true, attribute: 'form-selector' })
  formSelector = '';

  /**
   * Loading state that is reflected to an attribute for CSS targeting
   */
  @property({ type: Boolean, reflect: true, attribute: 'isloading' })
  isLoading = false;

  /**
   * Internal state to track if there are no results
   */
  @state()
  private hasNoResults = false;

  /**
   * Flag to indicate if the query attribute is being updated programmatically
   * When true, query changes won't trigger a fetch
   * When false (default), query changes will trigger a fetch
   * This is used to prevent infinite loops when updating the query attribute from the URL
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-updating-query' })
  isUpdatingQuery = false;

  /**
   * Container element for the fetched content in the light DOM
   */
  private contentContainer: HTMLElement | null = null;

  /**
   * MutationObserver to watch for the form appearing in the DOM
   */
  private formObserver: MutationObserver | null = null;

  /**
   * Reference to the current form being intercepted
   */
  private currentForm: HTMLFormElement | null = null;

  /**
   * Utility method for debug logging
   * Only logs when debug mode is enabled
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[nuvance-decoupled-search] ${message}`, ...args);
    }
  }

  /**
   * Component lifecycle callback when connected to DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog('Component connected to DOM');

    // Start async initialization
    this.initializeAsync().catch(error => {
      this.debugLog('Error during async initialization', { error }, true);
    });
  }

  /**
   * Asynchronously initializes the component
   * This allows other components to render while this one initializes
   */
  private async initializeAsync(): Promise<void> {
    this.debugLog('Starting async initialization');

    // First, set up the content container
    await this.setupContentContainerAsync();

    // Then run other initialization steps in parallel
    await Promise.all([
      // Initialize from URL parameters
      this.initializeFromUrlAsync(),

      // Set up URL handling and form interception in parallel
      Promise.all([
        this.setupUrlHandlingAsync(),
        this.setupFormInterceptionAsync(),
      ]),
    ]);

    // Check if we have a query but contentPath still has query parameters
    if (this.query && this.contentPath && this.contentPath.includes('?')) {
      this.debugLog('Initializing with query and contentPath with parameters', {
        query: this.query,
        contentPath: this.contentPath,
      });

      // Strip query parameters from contentPath
      const pathWithoutParams = this.contentPath.split('?')[0];
      this.debugLog('Resetting contentPath during initialization', {
        old: this.contentPath,
        new: pathWithoutParams,
      });
      this.contentPath = pathWithoutParams;
    }

    // Fetch initial content
    await this.fetchContent();
  }

  /**
   * Asynchronously sets up the content container
   */
  private async setupContentContainerAsync(): Promise<void> {
    return new Promise(resolve => {
      // Use requestAnimationFrame to defer content container setup
      requestAnimationFrame(() => {
        this.setupContentContainer();
        resolve();
      });
    });
  }

  /**
   * Asynchronously initializes from URL parameters
   */
  private async initializeFromUrlAsync(): Promise<void> {
    return new Promise(resolve => {
      // Use requestAnimationFrame to defer URL initialization
      requestAnimationFrame(() => {
        this.initializeFromUrl();
        resolve();
      });
    });
  }

  /**
   * Asynchronously sets up URL handling
   */
  private async setupUrlHandlingAsync(): Promise<void> {
    return new Promise(resolve => {
      // Use requestAnimationFrame to defer URL handling setup
      requestAnimationFrame(() => {
        this.setupUrlHandling();
        resolve();
      });
    });
  }

  /**
   * Asynchronously sets up form interception
   */
  private async setupFormInterceptionAsync(): Promise<void> {
    return new Promise(resolve => {
      // Use requestAnimationFrame to defer form interception setup
      requestAnimationFrame(() => {
        this.setupFormInterception();

        // Add a direct event listener to catch form submissions at the component level
        if (this.formSelector) {
          this.addEventListener(
            'submit',
            (event: Event) => {
              const target = event.target as HTMLElement;
              if (target.matches && target.matches(this.formSelector)) {
                this.debugLog('Form submission caught at component level', {
                  event,
                  target,
                });
                event.preventDefault();
                event.stopPropagation();
                this.processFormSubmission(target as HTMLFormElement);
                return false;
              }
              return true;
            },
            true
          );

          this.debugLog('Added component-level form submission handler', {
            formSelector: this.formSelector,
          });
        }

        resolve();
      });
    });
  }

  /**
   * Lifecycle callback when the element is disconnected from the DOM
   */
  disconnectedCallback(): void {
    this.debugLog('Disconnected from DOM');

    // Remove all event listeners
    this.removeEventListeners();

    // Disconnect the form observer if it exists
    if (this.formObserver) {
      this.formObserver.disconnect();
      this.formObserver = null;
    }

    super.disconnectedCallback();
  }

  /**
   * Handler for popstate events
   */
  private handlePopState = (): void => {
    this.debugLog('Popstate event detected');
    this.initializeFromUrl();
    this.fetchContent();
  };

  /**
   * Sets up the content container in the light DOM
   */
  private setupContentContainer(): void {
    // Check if the container already exists
    this.contentContainer = this.querySelector('.search-results-container');

    if (!this.contentContainer) {
      // Create a new container if it doesn't exist
      this.contentContainer = document.createElement('div');
      this.contentContainer.className = 'search-results-container';
      this.appendChild(this.contentContainer);
      this.debugLog('Created new content container in light DOM');
    } else {
      this.debugLog('Using existing content container in light DOM');
    }
  }

  /**
   * Sets up URL handling to maintain state and intercept link clicks
   */
  private setupUrlHandling(): void {
    this.debugLog('Setting up URL handling');

    // Set up event delegation for link clicks within the component
    this.addEventListener('click', this.handleLinkClick);

    // Listen for custom events from outline-pager-input (link clicks)
    this.addEventListener(
      'outline-pager-link-click',
      this.handlePagerLinkClick
    );

    // Listen for form submission events from outline-pager-input
    this.addEventListener(
      'outline-pager-input-navigate',
      this.handlePagerInputNavigate
    );

    // Initialize from current URL if applicable
    this.initializeFromUrl();

    // Listen for popstate events (browser back/forward buttons)
    window.addEventListener('popstate', this.handlePopState);
  }

  /**
   * Sets up form interception if a form selector is provided
   */
  private setupFormInterception(): void {
    if (!this.formSelector) {
      this.debugLog('No form selector provided, skipping form interception');
      return;
    }

    this.debugLog('Setting up form interception', {
      formSelector: this.formSelector,
    });

    // Find the form element within this component only
    const formElement = this.querySelector(
      this.formSelector
    ) as HTMLFormElement | null;

    if (!formElement) {
      this.debugLog(
        'Form element not found within component, setting up observer',
        {
          formSelector: this.formSelector,
        }
      );

      // Set up a MutationObserver to watch for the form appearing in the component
      this.setupFormObserver();
      return;
    }

    this.debugLog('Found form element within component', {
      formElement,
    });

    // Store a reference to the form for validation in the event handler
    this.currentForm = formElement;

    // Attach submit event listener with capture phase to ensure we catch it
    // Use a more direct approach to ensure the event is captured
    formElement.onsubmit = (event: SubmitEvent) => {
      this.debugLog('Form onsubmit triggered', { event, form: formElement });
      event.preventDefault();
      event.stopPropagation();
      this.processFormSubmission(formElement);
      return false; // Prevent default and stop propagation
    };

    // Also add a direct event listener as a backup
    formElement.addEventListener('submit', this.handleFormSubmit, true);

    // Also add a click listener to the submit button as a fallback
    const submitButton = formElement.querySelector('input[type="submit"]');
    if (submitButton) {
      this.debugLog('Found submit button, adding click listener', {
        submitButton,
      });
      submitButton.addEventListener(
        'click',
        this.handleSubmitButtonClick,
        true
      );
    }

    this.debugLog('Form interception set up successfully', {
      form: formElement,
      hasOnSubmit: typeof formElement.onsubmit === 'function',
    });
  }

  /**
   * Sets up a MutationObserver to watch for the form appearing in the component
   */
  private setupFormObserver(): void {
    // Disconnect any existing observer
    if (this.formObserver) {
      this.formObserver.disconnect();
    }

    // Create a new observer
    this.formObserver = new MutationObserver(_mutations => {
      // Check if the form has appeared within the component
      const formElement = this.querySelector(
        this.formSelector
      ) as HTMLFormElement | null;

      if (formElement) {
        this.debugLog('Form element appeared in component', {
          formSelector: this.formSelector,
          formElement,
        });

        // Store a reference to the form for validation in the event handler
        this.currentForm = formElement;

        // Attach submit event listener with capture phase
        // Use a more direct approach to ensure the event is captured
        formElement.onsubmit = (event: SubmitEvent) => {
          this.debugLog('Form onsubmit triggered from observer', {
            event,
            form: formElement,
          });
          event.preventDefault();
          event.stopPropagation();
          this.processFormSubmission(formElement);
          return false; // Prevent default and stop propagation
        };

        // Also add a direct event listener as a backup
        formElement.addEventListener('submit', this.handleFormSubmit, true);

        // Also add a click listener to the submit button as a fallback
        const submitButton = formElement.querySelector('input[type="submit"]');
        if (submitButton) {
          this.debugLog('Found submit button, adding click listener', {
            submitButton,
          });
          submitButton.addEventListener(
            'click',
            this.handleSubmitButtonClick,
            true
          );
        }

        this.debugLog('Form interception set up successfully from observer', {
          form: formElement,
          hasOnSubmit: typeof formElement.onsubmit === 'function',
        });

        // Disconnect the observer as we don't need it anymore
        this.formObserver?.disconnect();
        this.formObserver = null;
      }
    });

    // Start observing this component for changes
    this.formObserver.observe(this, {
      childList: true,
      subtree: true,
    });

    this.debugLog('Set up MutationObserver for form within component', {
      formSelector: this.formSelector,
    });
  }

  /**
   * Removes the form interception listener
   */
  private removeFormInterceptionListener(): void {
    if (!this.formSelector) {
      return;
    }

    // Remove event listener from the form if it exists
    const formElement = this.querySelector(
      this.formSelector
    ) as HTMLFormElement | null;

    if (formElement) {
      // Remove the onsubmit handler
      formElement.onsubmit = null;

      // Remove the event listener
      formElement.removeEventListener('submit', this.handleFormSubmit, true);

      // Also remove the click listener from the submit button
      const submitButton = formElement.querySelector('input[type="submit"]');
      if (submitButton) {
        submitButton.removeEventListener(
          'click',
          this.handleSubmitButtonClick,
          true
        );
      }

      this.debugLog('Removed form submit listeners', {
        formSelector: this.formSelector,
      });
    }

    // Clear the current form reference
    this.currentForm = null;

    // Disconnect the observer if it exists
    if (this.formObserver) {
      this.formObserver.disconnect();
      this.formObserver = null;
      this.debugLog('Disconnected form observer', {
        formSelector: this.formSelector,
      });
    }
  }

  /**
   * Handles form submission events
   */
  private handleFormSubmit = (event: Event): void => {
    // Verify this is our target form by checking against the stored reference
    const form = event.target as HTMLFormElement;

    // Only process if this is our specific form
    if (this.currentForm !== form) {
      this.debugLog('Form submit event ignored - not our target form', {
        eventTarget: form,
        currentForm: this.currentForm,
      });
      return;
    }

    this.debugLog('Form submit event intercepted', { event, form });

    // Prevent the default form submission
    event.preventDefault();
    event.stopPropagation();

    // Process the form data
    this.processFormSubmission(form);
  };

  /**
   * Handles submit button click events as a fallback
   */
  private handleSubmitButtonClick = (event: Event): void => {
    this.debugLog('Submit button clicked', { event });

    // Find the parent form
    const form = (event.target as HTMLElement).closest('form');
    if (!form) {
      this.debugLog('No parent form found for submit button', { event });
      return;
    }

    // Verify this is our target form by checking against the stored reference
    if (this.currentForm !== form) {
      this.debugLog('Submit button click ignored - not our target form', {
        buttonForm: form,
        currentForm: this.currentForm,
      });
      return;
    }

    // Prevent the default click behavior
    event.preventDefault();
    event.stopPropagation();

    // Process the form data
    this.processFormSubmission(form as HTMLFormElement);
  };

  /**
   * Constructs a search URL from form data
   * This method takes form data and constructs a URL that can be used for searching
   * It preserves important parameters like filters, pagination, and sorting
   */
  private constructSearchUrlFromForm(
    formData: FormData,
    formAction: string
  ): URL {
    // Start with the form action as the base URL
    let url: URL;
    try {
      // If the form action is a full URL, use it
      if (formAction.startsWith('http')) {
        url = new URL(formAction);
      } else if (formAction.startsWith('/')) {
        // If it's a path, combine with origin
        url = new URL(formAction, window.location.origin);
      } else {
        // If it's a relative path, combine with current path
        url = new URL(window.location.origin + '/' + formAction);
      }
    } catch (e) {
      // Fallback to endpoint if there's an issue with the form action
      url = new URL(this.endpoint, window.location.origin);
      this.debugLog('Error parsing form action, using endpoint instead', {
        formAction,
        endpoint: this.endpoint,
        error: e,
      });
    }

    // Add all form data to the URL
    formData.forEach((value, key) => {
      // Skip empty values
      if (value.toString().trim() !== '') {
        url.searchParams.set(key, value.toString());
      }
    });

    this.debugLog('Constructed search URL from form data', {
      formAction,
      url: url.toString(),
      searchParams: Array.from(url.searchParams.entries()),
    });

    return url;
  }

  /**
   * Processes a form submission
   */
  private processFormSubmission(form: HTMLFormElement): void {
    const formData = new FormData(form);
    const formAction = form.getAttribute('action') || '';
    const formMethod = form.getAttribute('method') || 'get';

    // Convert FormData to a plain object for easier debugging
    const formDataObj: Record<string, string> = {};
    formData.forEach((value, key) => {
      formDataObj[key] = value.toString();
    });

    // Extract specific fields of interest
    const searchQuery = formData.get(this.searchParam)?.toString() || '';

    // Determine if this is a Kyruus location filter form
    const isKyruusLocationForm =
      this.isKyruus &&
      form.id === 'kyruus-location-filter' &&
      (formData.has('distance') || formData.has('location'));

    // Analyze form elements for more detailed debugging
    const formElements: Record<string, Record<string, unknown>> = {};
    const formInputs = Array.from(form.elements);
    formInputs.forEach((element: Element) => {
      if ((element as HTMLInputElement).name) {
        const inputElement = element as
          | HTMLInputElement
          | HTMLSelectElement
          | HTMLTextAreaElement;
        const elementType = inputElement.type || element.tagName.toLowerCase();
        const elementValue = inputElement.value;
        const elementChecked =
          'checked' in inputElement ? inputElement.checked : undefined;

        formElements[inputElement.name] = {
          type: elementType,
          value: elementValue,
          checked:
            elementType === 'checkbox' || elementType === 'radio'
              ? elementChecked
              : undefined,
          id: inputElement.id || undefined,
          className: inputElement.className || undefined,
        };
      }
    });

    // Categorize form fields by type
    const categorizedFields: Record<string, Record<string, string>> = {
      search: {},
      filter: {},
      geolocation: {},
      pagination: {},
      sort: {},
      other: {},
    };

    // Analyze and categorize each form field
    for (const [key, value] of formData.entries()) {
      const stringValue = value.toString();

      if (key === this.searchParam) {
        categorizedFields.search[key] = stringValue;
      } else if (key.startsWith('field_geolocation')) {
        categorizedFields.geolocation[key] = stringValue;
      } else if (key.includes('location') || key.includes('distance')) {
        categorizedFields.geolocation[key] = stringValue;
      } else if (key.startsWith('f[') || key.includes('filter')) {
        categorizedFields.filter[key] = stringValue;
      } else if (key.includes('page') || key.includes('pager')) {
        categorizedFields.pagination[key] = stringValue;
      } else if (key.includes('sort') || key.includes('order')) {
        categorizedFields.sort[key] = stringValue;
      } else {
        categorizedFields.other[key] = stringValue;
      }
    }

    // Construct what the URL would have been if the form was submitted normally
    let targetUrl = formAction;
    if (formMethod.toLowerCase() === 'get') {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });
      targetUrl += '?' + params.toString();
    }

    // Create a URL object for more detailed analysis
    let parsedTargetUrl: URL;
    try {
      parsedTargetUrl = new URL(targetUrl, window.location.origin);
    } catch (e) {
      parsedTargetUrl = new URL(window.location.origin + targetUrl);
    }

    // For Kyruus location filter forms, preserve the current keyword if it exists
    if (isKyruusLocationForm && this.query) {
      const preserveCurrentQuery = this.query;
      this.debugLog(
        'Processing Kyruus location filter form with existing query',
        {
          formId: form.id,
          preserveCurrentQuery,
          formData: formDataObj,
        }
      );

      // Add the current query to the form data for URL construction
      formData.append(this.searchParam, preserveCurrentQuery);
    }

    // Construct a search URL from the form data
    const searchUrl = this.constructSearchUrlFromForm(formData, formAction);

    // Log detailed form submission information
    this.debugLog('Processed form submission', {
      form,
      formAction,
      formMethod,
      formData: formDataObj,
      formElements,
      categorizedFields,
      searchQuery,
      targetUrl,
      parsedUrl: {
        href: parsedTargetUrl.href,
        origin: parsedTargetUrl.origin,
        pathname: parsedTargetUrl.pathname,
        searchParams: Array.from(parsedTargetUrl.searchParams.entries()),
      },
      searchUrl: searchUrl.toString(),
    });

    // Update the content path with the search URL
    // This preserves all form parameters in the content path
    if (this.contentPathParam) {
      this.contentPath = searchUrl.pathname + searchUrl.search;

      // Update the URL to reflect the new content path
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set(this.contentPathParam, this.contentPath);

      // Update browser history without triggering a page reload
      window.history.pushState({}, '', currentUrl.toString());

      this.debugLog('Updated content path and URL from form submission', {
        contentPath: this.contentPath,
        contentPathParam: this.contentPathParam,
        url: currentUrl.toString(),
      });
    }

    // For Kyruus location filter forms, don't reset the query
    if (isKyruusLocationForm) {
      this.debugLog('Preserving existing query for Kyruus location filter', {
        query: this.query,
      });

      // Just fetch the content without changing the query
      this.fetchContent();
    }
    // Set the query attribute to trigger a search
    else if (searchQuery !== this.query) {
      this.debugLog('Setting query from form submission', {
        oldQuery: this.query,
        newQuery: searchQuery,
      });

      // Set isUpdatingQuery to false to ensure the query change triggers a fetch
      this.isUpdatingQuery = false;
      this.query = searchQuery;
    } else {
      // If the query hasn't changed, we still need to fetch content
      this.debugLog('Query unchanged, fetching content directly', {
        query: this.query,
      });
      this.fetchContent();
    }
  }

  /**
   * Removes all event listeners
   */
  private removeEventListeners(): void {
    window.removeEventListener('popstate', this.handlePopState);
    this.removeEventListener('click', this.handleLinkClick);
    this.removeEventListener(
      'outline-pager-link-click',
      this.handlePagerLinkClick
    );
    this.removeEventListener(
      'outline-pager-input-navigate',
      this.handlePagerInputNavigate
    );

    // Remove form submit listener if it was set up
    this.removeFormInterceptionListener();
  }

  /**
   * Handler for link click events
   */
  private handleLinkClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    const link = target.closest('a');

    if (link && this.shouldInterceptLink(link)) {
      event.preventDefault();
      this.debugLog('Intercepted link click', { href: link.href });
      this.handleLinkNavigation(link.href);
    }
  };

  /**
   * Handler for outline-pager-link-click events
   */
  private handlePagerLinkClick = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const url = customEvent.detail?.url;

    if (url) {
      event.preventDefault();
      this.debugLog('Intercepted outline-pager-link-click event', { url });
      this.handleLinkNavigation(url);
    }
  };

  /**
   * Handler for outline-pager-input-navigate events
   */
  private handlePagerInputNavigate = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const url = customEvent.detail?.url;
    const page = customEvent.detail?.page;

    this.debugLog('Received outline-pager-input-navigate event', {
      event: customEvent,
      detail: customEvent.detail,
      url,
      page,
      eventTarget: event.target,
      eventCurrentTarget: event.currentTarget,
      eventPath: event.composedPath?.() || [],
    });

    if (url) {
      event.preventDefault();
      this.debugLog('Handling outline-pager-input-navigate event', {
        url,
        page,
      });
      this.handleLinkNavigation(url);
    } else {
      this.debugLog('No URL in outline-pager-input-navigate event', {
        detail: customEvent.detail,
      });
    }
  };

  /**
   * Initializes the component state from the current URL
   */
  private initializeFromUrl(): void {
    // Get the current value of the contentPathParam property
    const paramName =
      this.getAttribute('content-path-param') || this.contentPathParam;

    // If no parameter name is set, we can't read from the URL
    if (!paramName) {
      this.debugLog(
        'No content path parameter name set, skipping URL initialization'
      );
      return;
    }

    const url = new URL(window.location.href);
    const searchQuery = url.searchParams.get(this.searchParam);
    const contentPath = url.searchParams.get(paramName);

    this.debugLog('Initializing from URL', {
      url: url.toString(),
      searchParam: this.searchParam,
      searchQuery,
      contentPathParam: paramName,
      attributeValue: this.getAttribute('content-path-param'),
      contentPath: contentPath,
    });

    if (searchQuery) {
      // Set the flag before updating the query
      this.isUpdatingQuery = true;
      this.query = searchQuery;
      this.debugLog('Set query from URL', {
        query: this.query,
        isUpdatingQuery: true,
      });
    }

    if (contentPath) {
      this.contentPath = contentPath;
      this.debugLog('Set content path from URL', {
        contentPath: this.contentPath,
      });
    }
  }

  /**
   * Fetches content from the API endpoint
   */
  private async fetchContent(): Promise<void> {
    if (!this.endpoint || !this.contentContainer) {
      this.debugLog('Cannot fetch content - missing endpoint or container', {
        endpoint: this.endpoint,
        contentContainer: !!this.contentContainer,
      });
      return;
    }

    this.isLoading = true;
    this.hasNoResults = false;
    this.debugLog('Fetching content', {
      endpoint: this.endpoint,
      query: this.query,
      contentPath: this.contentPath,
      contentPathParam: this.contentPathParam,
    });

    try {
      // Construct the URL with query parameters
      let url: URL;

      if (this.contentPath) {
        // If we have a content path, use it to construct the URL
        // Make sure to handle both absolute and relative paths
        this.debugLog('Content path details', {
          contentPath: this.contentPath,
          startsWithHttp: this.contentPath.startsWith('http'),
          startsWithApi: this.contentPath.startsWith('/api/'),
          startsWithSlash: this.contentPath.startsWith('/'),
        });

        // Check if contentPath has query parameters and we have a query
        let pathToUse = this.contentPath;
        const preservedParams = new URLSearchParams();

        if (this.contentPath.includes('?')) {
          // Extract the base path and query parameters
          const [basePath, queryString] = this.contentPath.split('?', 2);
          const contentPathParams = new URLSearchParams(queryString);

          // Check if this is a filter click (contentPath has our query and other parameters)
          const isFilterClick =
            contentPathParams.has(this.searchParam) &&
            contentPathParams.get(this.searchParam) === this.query;

          if (isFilterClick) {
            // This is a filter click, use the contentPath as is
            this.debugLog('Detected filter click, using full content path', {
              contentPath: this.contentPath,
              query: this.query,
            });
            pathToUse = this.contentPath;
          } else if (this.query && contentPathParams.has(this.searchParam)) {
            // If we have a query and the contentPath already has a search parameter,
            // we'll use our query instead but preserve other parameters (like filters)
            pathToUse = basePath;

            // Remove the search parameter as we'll add it back later
            contentPathParams.delete(this.searchParam);

            // Preserve the remaining parameters (filters, etc.)
            contentPathParams.forEach((value, key) => {
              // Don't preserve tab state parameters in the API request
              if (!key.startsWith('tab-state-')) {
                preservedParams.append(key, value);
              }
            });

            this.debugLog('Preserving parameters from content path', {
              originalPath: this.contentPath,
              basePath: pathToUse,
              preservedParams: Array.from(preservedParams.entries()),
              query: this.query,
            });
          } else {
            // If we don't have a query or the contentPath doesn't have a search parameter,
            // just use the contentPath as is
            pathToUse = this.contentPath;
          }
        }

        // Now construct the URL with the path
        if (pathToUse.startsWith('http')) {
          // Full URL - check if it's from the current domain for security
          url = new URL(pathToUse);

          // Only allow requests to the current domain for security
          if (url.hostname !== window.location.hostname) {
            this.debugLog(
              'Fetch aborted - security restriction: only current domain is allowed',
              {
                requestedDomain: url.hostname,
                currentDomain: window.location.hostname,
              }
            );
            throw new Error(
              `Security restriction: Only the current domain (${window.location.hostname}) is allowed`
            );
          }

          this.debugLog('Using full URL from content path', {
            url: url.toString(),
          });
        } else if (pathToUse.startsWith('/api/')) {
          // API path - use as is with origin
          url = new URL(pathToUse, window.location.origin);
          this.debugLog('Using API path from content path', {
            contentPath: pathToUse,
            origin: window.location.origin,
            url: url.toString(),
          });
        } else {
          // Other path - prepend endpoint if it doesn't already include it
          const path = pathToUse.startsWith('/') ? pathToUse : `/${pathToUse}`;
          const endpointPath = this.endpoint.startsWith('/')
            ? this.endpoint
            : `/${this.endpoint}`;

          this.debugLog('Path construction details', {
            path,
            endpointPath,
            includes: path.includes(endpointPath),
          });

          if (path.includes(endpointPath)) {
            url = new URL(path, window.location.origin);
            this.debugLog('Path already includes endpoint', {
              url: url.toString(),
            });
          } else {
            url = new URL(endpointPath + path, window.location.origin);
            this.debugLog('Prepending endpoint to path', {
              url: url.toString(),
            });
          }
        }

        // Add the query if we have one
        if (this.query) {
          url.searchParams.set(this.searchParam, this.query);
          this.debugLog('Added search query to URL from contentPath', {
            param: this.searchParam,
            value: this.query,
            url: url.toString(),
          });
        }

        // Add any preserved parameters (filters, etc.)
        preservedParams.forEach((value, key) => {
          url.searchParams.set(key, value);
          this.debugLog('Added preserved parameter to URL', {
            key,
            value,
            url: url.toString(),
          });
        });

        this.debugLog('Using content path for fetch URL', {
          contentPath: this.contentPath,
          url: url.toString(),
          searchParams: Array.from(url.searchParams.entries()),
        });
      } else {
        // Otherwise use the endpoint
        url = new URL(this.endpoint, window.location.origin);
        this.debugLog('Using endpoint for fetch URL', {
          endpoint: this.endpoint,
          url: url.toString(),
        });

        // Add search query if available
        if (this.query) {
          url.searchParams.set(this.searchParam, this.query);
          this.debugLog('Added search query to URL', {
            param: this.searchParam,
            value: this.query,
            url: url.toString(),
          });

          // If we have a non-empty query, we don't need to copy other parameters
          // as this is a new search (they were already reset in resetSearchParameters)
          if (this.query.trim() !== '') {
            this.debugLog(
              'Skipping copying other parameters for new search query'
            );
          } else {
            // Copy any other relevant query parameters from the current URL
            this.copyRelevantQueryParams(url);
          }
        } else {
          // Copy any other relevant query parameters from the current URL
          this.copyRelevantQueryParams(url);
        }
      }

      // Final URL security check - ensure we're only fetching from the current domain
      if (url.hostname !== window.location.hostname) {
        this.debugLog(
          'Fetch aborted - security restriction: only current domain is allowed',
          {
            requestedDomain: url.hostname,
            currentDomain: window.location.hostname,
          }
        );
        throw new Error(
          `Security restriction: Only the current domain (${window.location.hostname}) is allowed`
        );
      }

      this.debugLog('Fetching from URL', {
        url: url.toString(),
        searchParams: Array.from(url.searchParams.entries()),
        pathname: url.pathname,
      });

      const response = await fetch(url.toString());

      if (!response.ok) {
        this.debugLog('Fetch failed', {
          status: response.status,
          statusText: response.statusText,
          url: url.toString(),
        });
        throw new Error(`Failed to fetch content: ${response.statusText}`);
      }

      this.debugLog('Received response', {
        status: response.status,
        url: url.toString(),
      });
      const html = await response.text();
      this.parseAndSetContent(html);

      // Update the content path parameter in the URL if we have one
      if (this.contentPathParam) {
        // Get the current URL
        const currentUrl = new URL(window.location.href);

        // Create a new content path that includes the query if we have one
        let newContentPath = url.pathname;

        // Add the query and filter parameters to the content path
        if (url.search) {
          newContentPath += url.search;

          // Check if the URL has a search parameter that might be different from our query attribute
          const urlParams = new URLSearchParams(url.search);
          const urlQuery = urlParams.get(this.searchParam);

          // If the URL has a search parameter and it's different from our query attribute, update the attribute
          if (urlQuery !== null && urlQuery !== this.query) {
            this.debugLog('Updating query attribute to match URL', {
              oldQuery: this.query,
              newQuery: urlQuery,
            });

            // Set the flag before updating the attribute
            this.isUpdatingQuery = true;
            this.query = urlQuery;
          }
        }

        // Update the content path parameter in the URL
        currentUrl.searchParams.set(this.contentPathParam, newContentPath);

        // Update the contentPath property
        this.contentPath = newContentPath;

        // Update browser history without triggering a page reload
        window.history.pushState({}, '', currentUrl.toString());

        this.debugLog('Updated content path parameter in URL', {
          paramName: this.contentPathParam,
          value: newContentPath,
          url: currentUrl.toString(),
        });
      }
    } catch (error) {
      this.debugLog('Error fetching search content', { error }, true);
      this.clearContent();
      this.hasNoResults = true;
    } finally {
      this.isLoading = false;
      this.debugLog('Fetch completed', {
        isLoading: this.isLoading,
        hasNoResults: this.hasNoResults,
      });
    }
  }

  /**
   * Copies relevant query parameters from the current URL to the API request URL
   */
  private copyRelevantQueryParams(targetUrl: URL): void {
    const currentUrl = new URL(window.location.href);
    // Get the current value of the contentPathParam property
    const paramName =
      this.getAttribute('content-path-param') || this.contentPathParam;

    this.debugLog('Copying query parameters', {
      from: currentUrl.toString(),
      to: targetUrl.toString(),
      contentPathParam: paramName,
    });

    // Copy all query parameters except those that should be handled specially
    currentUrl.searchParams.forEach((value, key) => {
      // Skip the search parameter, content path parameters, and tab state parameters
      if (
        key !== this.searchParam &&
        (paramName === null || key !== paramName) &&
        !key.startsWith('content_path_') &&
        !key.startsWith('tab-state-')
      ) {
        targetUrl.searchParams.set(key, value);
        this.debugLog('Copied query parameter', { key, value });
      }
    });
  }

  /**
   * Resets search parameters when a query changes
   * This is called when:
   * 1. The query property changes to a non-empty value (new search)
   * 2. The query property is explicitly set to an empty value (search reset)
   *
   * It effectively starts a new search from scratch (page 1, no filters)
   */
  private resetSearchParameters(): void {
    this.debugLog('Resetting search parameters for new query');

    // Reset the contentPath to ensure we don't use old filters
    if (this.contentPath && this.contentPath.includes('?')) {
      // If contentPath has query parameters, strip them
      const pathWithoutParams = this.contentPath.split('?')[0];
      this.debugLog('Resetting contentPath', {
        old: this.contentPath,
        new: pathWithoutParams,
      });
      this.contentPath = pathWithoutParams;
    }

    // Update only this component's parameter in the URL
    const url = new URL(window.location.href);

    // Get the content path parameter name
    const paramName = this.contentPathParam;

    if (paramName) {
      // If we have a content path parameter, update it with the base path
      // This preserves the state of other nuvance-decoupled-search instances
      const basePath = this.endpoint.startsWith('/')
        ? this.endpoint
        : `/${this.endpoint}`;
      url.searchParams.set(paramName, basePath);

      this.debugLog('Updated content path parameter in URL', {
        paramName,
        value: basePath,
        url: url.toString(),
      });

      // Update browser history without triggering a page reload
      window.history.pushState({}, '', url.toString());
      this.debugLog('Updated URL for new search', { url: url.toString() });
    } else {
      this.debugLog('No content path parameter set, skipping URL update');
    }
  }

  /**
   * Parses the HTML response and extracts the relevant content
   */
  private parseAndSetContent(html: string): void {
    if (!this.contentContainer) {
      return;
    }

    // Create a temporary element to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find the content using the selector
    const content = doc.querySelector(this.contentSelector);

    if (!content) {
      this.debugLog(
        `Content selector "${this.contentSelector}" not found in response`,
        {},
        true
      );
      this.clearContent();
      this.hasNoResults = true;
      return;
    }

    // Clear existing content
    this.clearContent();

    // Add the new content
    this.contentContainer.innerHTML = content.innerHTML;
    this.hasNoResults = this.contentContainer.innerHTML.trim() === '';

    this.debugLog('Content updated', {
      hasContent: !this.hasNoResults,
      contentLength: this.contentContainer.innerHTML.length,
    });

    // Dispatch an event to notify that content has been updated
    // The wrapper component will handle extracting counts and updating tab buttons
    this.dispatchEvent(
      new CustomEvent('search-content-updated', {
        bubbles: true,
        composed: true,
        detail: {
          sourceId: this.id || 'unknown-search-component',
          isEmpty: this.hasNoResults,
        },
      })
    );
  }

  /**
   * Clears the content container
   */
  private clearContent(): void {
    if (this.contentContainer) {
      this.debugLog('Clearing content container', {
        childCount: this.contentContainer.childNodes.length,
      });

      while (this.contentContainer.firstChild) {
        this.contentContainer.removeChild(this.contentContainer.firstChild);
      }
    }
  }

  /**
   * Lifecycle callback when properties change
   */
  updated(changedProperties: Map<string, unknown>): void {
    super.updated(changedProperties);

    if (changedProperties.has('contentPathParam')) {
      const oldValue = changedProperties.get('contentPathParam');
      this.debugLog('contentPathParam changed', {
        oldValue,
        newValue: this.contentPathParam,
        attributes: Array.from(this.attributes)
          .map(attr => `${attr.name}=${attr.value}`)
          .join(', '),
      });
    }

    // Handle changes to the form selector
    if (changedProperties.has('formSelector')) {
      const oldValue = changedProperties.get('formSelector') as string;
      this.debugLog('formSelector changed', {
        oldValue,
        newValue: this.formSelector,
      });

      // Remove old form listener if there was one
      if (oldValue) {
        const oldFormElement = this.querySelector(
          oldValue
        ) as HTMLFormElement | null;
        if (oldFormElement) {
          // Remove the onsubmit handler
          oldFormElement.onsubmit = null;

          // Remove the event listener
          oldFormElement.removeEventListener(
            'submit',
            this.handleFormSubmit,
            true
          );

          // Also remove the click listener from the submit button
          const oldSubmitButton = oldFormElement.querySelector(
            'input[type="submit"]'
          );
          if (oldSubmitButton) {
            oldSubmitButton.removeEventListener(
              'click',
              this.handleSubmitButtonClick,
              true
            );
          }

          this.debugLog('Removed form submit listener from old selector', {
            oldSelector: oldValue,
          });
        }
      }

      // Set up new form interception
      this.setupFormInterception();
    }

    // Re-fetch content if query changes
    if (changedProperties.has('query') && this.isConnected) {
      const newQuery = this.query;
      const oldQuery = (changedProperties.get('query') as string) || '';

      this.debugLog('Query changed', {
        oldQuery,
        newQuery,
        isExplicitReset: newQuery === '' && oldQuery && oldQuery.trim() !== '',
        isUpdatingQuery: this.isUpdatingQuery,
      });

      // Skip fetching if we're just updating the attribute programmatically
      // But only if isUpdatingQuery is true - if it's explicitly set to false, we should process the query
      if (this.isUpdatingQuery === true) {
        this.debugLog(
          'Skipping fetch because query was updated programmatically'
        );
        this.isUpdatingQuery = false;
        return;
      }

      // If we get here, either:
      // 1. isUpdatingQuery is false (default case or explicitly set by input component)
      // 2. isUpdatingQuery is undefined (shouldn't happen, but just in case)
      this.debugLog('Processing query change', {
        reason:
          this.isUpdatingQuery === false
            ? 'isUpdatingQuery explicitly false'
            : 'normal query change',
        oldQuery,
        newQuery,
      });

      // Reset search parameters if:
      // 1. A new non-empty query is set (new search)
      // 2. An empty query is explicitly set (search reset) and the old query was non-empty
      if (
        (newQuery && newQuery.trim() !== '') ||
        (newQuery === '' && oldQuery && oldQuery.trim() !== '')
      ) {
        this.debugLog('Resetting search parameters', {
          reason: this.getResetReason(false, newQuery),
          oldQuery,
          newQuery,
        });
        this.resetSearchParameters();
      }

      this.fetchContent();
    }
  }

  /**
   * Gets a descriptive reason for resetting search parameters
   */
  private getResetReason(isUserQuery: boolean, newQuery: string): string {
    if (isUserQuery) {
      return 'user query';
    } else if (newQuery) {
      return 'new search query';
    } else {
      return 'explicit empty query';
    }
  }

  /**
   * Handles navigation when a link is clicked
   */
  private handleLinkNavigation(url: string): void {
    // Get the current value of the contentPathParam property
    const paramName =
      this.getAttribute('content-path-param') || this.contentPathParam;

    // If no parameter name is set, we can't update the URL
    if (!paramName) {
      this.debugLog(
        'No content path parameter name set, using default behavior'
      );
      // Just navigate to the URL directly
      window.location.href = url;
      return;
    }

    this.debugLog('Handling link navigation', {
      url,
      contentPathParam: this.contentPathParam,
      attributeValue: this.getAttribute('content-path-param'),
      paramName,
      endpoint: this.endpoint,
      attributes: Array.from(this.attributes)
        .map(attr => `${attr.name}=${attr.value}`)
        .join(', '),
    });

    try {
      // Extract the path from the clicked URL
      const targetUrl = new URL(url, window.location.origin);

      // Security check - ensure we're only navigating to the current domain
      if (targetUrl.hostname !== window.location.hostname) {
        this.debugLog(
          'Navigation aborted - security restriction: only current domain is allowed',
          {
            requestedDomain: targetUrl.hostname,
            currentDomain: window.location.hostname,
          }
        );
        throw new Error(
          `Security restriction: Only the current domain (${window.location.hostname}) is allowed for navigation`
        );
      }

      this.debugLog('Parsed URL details', {
        originalUrl: url,
        parsedUrl: targetUrl.toString(),
        protocol: targetUrl.protocol,
        host: targetUrl.host,
        pathname: targetUrl.pathname,
        search: targetUrl.search,
        hash: targetUrl.hash,
      });

      // Check if the URL is already an API endpoint URL (from outline-pager-input)
      const isApiUrl = targetUrl.pathname.includes(this.endpoint);

      this.debugLog('API URL check', {
        isApiUrl,
        urlPathname: targetUrl.pathname,
        endpoint: this.endpoint,
        includes: targetUrl.pathname.includes(this.endpoint),
      });

      // Store the original query value
      const originalQuery = this.query;

      // Check if the target URL has a search parameter that matches our searchParam
      const targetUrlParams = new URLSearchParams(targetUrl.search);
      const hasSearchParam = targetUrlParams.has(this.searchParam);
      const searchParamValue = targetUrlParams.get(this.searchParam);

      // If the target URL has a search parameter, update our query property
      if (hasSearchParam && searchParamValue) {
        this.isUpdatingQuery = true;
        this.query = searchParamValue;
        this.debugLog('Updated query from target URL', {
          oldQuery: originalQuery,
          newQuery: this.query,
          searchParam: this.searchParam,
          isUpdatingQuery: true,
        });
      } else if (hasSearchParam && searchParamValue === '') {
        // If the search parameter is present but empty, set query to empty string
        this.isUpdatingQuery = true;
        this.query = '';
        this.debugLog('Reset query to empty string', {
          oldQuery: originalQuery,
          searchParam: this.searchParam,
          isUpdatingQuery: true,
        });
      } else if (this.query && !hasSearchParam) {
        // If we have a query but the target URL doesn't have a search parameter,
        // check if this is a filter or pagination link that should preserve the query
        const isFilterOrPagination =
          targetUrl.pathname.includes(this.endpoint) &&
          (targetUrl.search.includes('f%5B') ||
            targetUrl.search.includes('page='));

        if (!isFilterOrPagination) {
          // If it's not a filter or pagination link, reset the query
          this.isUpdatingQuery = true;
          this.query = '';
          this.debugLog('Reset query for non-filter/pagination link', {
            oldQuery: originalQuery,
            url: targetUrl.toString(),
            isUpdatingQuery: true,
          });
        } else {
          this.debugLog('Preserving query for filter/pagination link', {
            query: this.query,
            url: targetUrl.toString(),
          });
        }
      }

      if (isApiUrl) {
        this.debugLog('URL is already an API endpoint URL', { url });
        // Extract just the pathname and search from the API URL
        this.contentPath = targetUrl.pathname + targetUrl.search;
      } else {
        // For regular links, extract the path and search
        this.contentPath = targetUrl.pathname + targetUrl.search;
      }

      this.debugLog('Content path set', {
        contentPath: this.contentPath,
        isApiUrl,
      });

      // Update only this component's parameter in the URL
      const currentUrl = new URL(window.location.href);

      this.debugLog('Setting URL parameter', {
        paramName,
        contentPath: this.contentPath,
        isApiUrl,
        currentUrl: currentUrl.toString(),
        attributes: Array.from(this.attributes)
          .map(attr => `${attr.name}=${attr.value}`)
          .join(', '),
      });

      // Update only this component's parameter in the URL
      currentUrl.searchParams.set(paramName, this.contentPath);

      // Update URL without page reload
      window.history.pushState({}, '', currentUrl.toString());
      this.debugLog('Updated URL', {
        oldUrl: window.location.href,
        newUrl: currentUrl.toString(),
      });

      // Fetch new content based on the content path
      this.fetchContent();
    } catch (error) {
      this.debugLog('Error handling link navigation', { error, url });
      console.error('Error handling link navigation:', error);
    }
  }

  /**
   * Determines if a link click should be intercepted
   */
  private shouldInterceptLink(link: HTMLAnchorElement): boolean {
    // Check if the link is related to search functionality
    const linkUrl = new URL(link.href);

    // Check if the link points to the same endpoint as this instance
    const shouldIntercept = linkUrl.pathname.includes(this.endpoint);

    this.debugLog('Checking if link should be intercepted', {
      href: link.href,
      endpoint: this.endpoint,
      contentPathParam: this.contentPathParam,
      shouldIntercept,
    });

    return shouldIntercept;
  }

  /**
   * Renders the component
   */
  render(): TemplateResult {
    this.debugLog('Rendering component', {
      isLoading: this.isLoading,
      hasNoResults: this.hasNoResults,
    });

    return html`
      ${this.isLoading
        ? html`<div class="loading-spinner" aria-live="polite" role="status">
            <span class="visually-hidden">Loading search results...</span>
          </div>`
        : nothing}
      ${this.hasNoResults
        ? html`<div class="no-results">No search results available</div>`
        : nothing}

      <slot></slot>
    `;
  }
}

// Type declaration for global HTML element map
declare global {
  interface HTMLElementTagNameMap {
    'nuvance-decoupled-search': NuvanceDecoupledSearch;
  }
}
